import React, { memo } from 'react';
import styled from 'styled-components';
import { Button } from 'react-bootstrap';
import { Block, BlockProduct } from '@views/components/block/Block';
import CardBody from '@views/components/product/card/CardBody';
import CardInfo from '@views/components/product/card/CardInfo';
import CardName from '@views/components/product/card/CardName';
import { LazyLoadImage } from '@views/components/lazy-image';
import useLayoutContext from '@views/layouts/hooks/use-context';
import get from 'lodash/get';

// Import Swiper React components
import SwiperCore, {
  Navigation,
  Pagination,
  Virtual,
  A11y,
  Mousewheel,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/swiper.scss';
import 'swiper/components/navigation/navigation.scss';
import 'swiper/components/pagination/pagination.scss';
import useBreakpoint, { Breakpoints } from '@hooks/use-break-point';
import cn from 'classnames';
import {
  EVENT_CLASS_NAME,
  EVENT_ACTION_NAME,
  EVENT_CATEGORY,
  EVENT_LABEL,
} from '@helpers/events';

// install Virtual module
SwiperCore.use([Navigation, Pagination, Virtual, A11y, Mousewheel]);

const ButtonAction = styled(Button)`
  display: block;
  width: 100%;
  margin-bottom: -25px;
`;

type ItemProps = {
  item: {
    id: number;
    name: string;
    image: string;
  };
  isSelected: boolean;
  onSelect: (item: any) => void;
};

const SwiperBlock = styled.div`
  .product-card {
    height: 300px;
  }
`;

const ImageBlock = styled.div`
  img {
    height: 160px;
    padding: 15px;
    object-fit: scale-down;
  }
`;

const Item = memo<ItemProps>(({ item, isSelected, onSelect }) => {
  return (
    <div className="product-card">
      <ImageBlock className="product-card__image-block">
        <LazyLoadImage image={{ src: item.image, className: 'card-img-top' }} />
      </ImageBlock>
      <CardBody>
        <CardInfo>
          <CardName>{item.name}</CardName>
        </CardInfo>
        <ButtonAction
          variant={isSelected ? 'secondary' : 'primary'}
          onClick={() => {
            onSelect(item);

            dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            dataLayer.push({
              event: 'promotionClick',
              ecommerce: {
                promoClick: {
                  promotions: [
                    {
                      id: item.id, // Name or ID is required.
                      name: item.name,
                    },
                  ],
                },
              },
            });
          }}
          disabled={isSelected}
          className={cn(EVENT_CLASS_NAME)}
          data-category={EVENT_CATEGORY.SELECT_GIFT}
          data-action={EVENT_ACTION_NAME}
          data-label={item.name}
        >
          Chọn
        </ButtonAction>
      </CardBody>
    </div>
  );
});

export const ProductList = memo(() => {
  const {
    checkout: { data, selectedGift, handleChooseGift },
  } = useLayoutContext();
  const breakpoint = useBreakpoint();
  const isMobile = [Breakpoints.xs, Breakpoints.sm].includes(breakpoint);

  const giftList = get(data, 'checkout.promotion.gift', []);
  if (!giftList || giftList.length === 0) {
    return null;
  }

  return (
    <Block isSection className="mb-4">
      <Block.Header className="mb-4">
        <Block.Title black as="h4">
          Quà tặng
        </Block.Title>
      </Block.Header>

      <SwiperBlock>
        <Swiper
          spaceBetween={-1}
          navigation={!isMobile}
          pagination
          passiveListeners
          breakpoints={{
            375: {
              slidesPerView: 2,
              slidesPerGroup: 2,
              navigation: false,
            },
            567: {
              slidesPerView: 2,
              slidesPerGroup: 2,
              navigation: false,
            },
            768: {
              slidesPerView: 4,
              slidesPerGroup: 4,
            },
            992: {
              slidesPerView: 6,
              slidesPerGroup: 4,
            },
          }}
          autoHeight
        >
          {giftList.map((item: any, index: number) => (
            <SwiperSlide key={`${item.id}}_${index}`}>
              <Item
                item={item}
                isSelected={get(item, 'id') === get(selectedGift, 'id')}
                onSelect={handleChooseGift}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperBlock>
    </Block>
  );
});
ProductList.displayName = 'ProductList';

export default ProductList;
